import type { StorageBlobHttpHeaders, StorageResourceProperties } from '../../../../types/azureStorage';

const headerKeys: Array<keyof StorageBlobHttpHeaders> = ['contentType', 'contentLanguage', 'contentEncoding', 'contentDisposition', 'cacheControl', 'contentMD5'];

export function headersFromProperties(value: StorageResourceProperties): StorageBlobHttpHeaders {
  const headers: StorageBlobHttpHeaders = {};
  for (const key of headerKeys) {
    const raw = value.properties[key];
    if (typeof raw === 'string' && raw) headers[key] = raw;
  }
  return headers;
}

export function equalStringMap(left: Record<string, string>, right: Record<string, string>, foldKeys = false): boolean {
  const normalize = (value: Record<string, string>) => Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [foldKeys ? key.toLowerCase() : key, entry]).sort(([a], [b]) => a.localeCompare(b)),
  );
  return JSON.stringify(normalize(left)) === JSON.stringify(normalize(right));
}

export function equalHeaders(left: StorageBlobHttpHeaders, right: StorageBlobHttpHeaders): boolean {
  const defined = (value: StorageBlobHttpHeaders) => headerKeys.filter((key) => value[key] !== undefined).map((key) => [key, value[key]]);
  return JSON.stringify(defined(left)) === JSON.stringify(defined(right));
}

/**
 * One editable key/value row (review C F3). Rows keep a stable id, so a key edit never re-keys the list and a map is
 * built only from a valid set of rows: a rename onto another row's key is refused instead of silently dropping that row.
 */
export type KeyValueRow = { id: string; key: string; value: string };
export type KeyValueRules = { caseInsensitiveKeys: boolean };
export type RowsResult = { ok: true; value: Record<string, string> } | { ok: false; message: string; rowIds: string[] };

let rowSequence = 0;
const newRowId = (): string => `kv-${(rowSequence += 1)}`;

/** Rows for a map, reusing the id of a previous row with the same key so inputs keep their identity across refreshes. */
export function rowsFromMap(map: Record<string, string>, previous: KeyValueRow[] = []): KeyValueRow[] {
  const byKey = new Map(previous.map((row) => [row.key, row.id]));
  return Object.entries(map).map(([key, value]) => ({ id: byKey.get(key) ?? newRowId(), key, value }));
}

/**
 * The map is built with `Object.fromEntries` (review D RD1): an indexed assignment `value[key] = …` onto `{}` would treat a
 * valid "__proto__" metadata name or tag key as the prototype setter and silently drop that entry from the saved map.
 */
export function mapFromRows(rows: KeyValueRow[], rules: KeyValueRules): RowsResult {
  const seen = new Map<string, string>();
  for (const row of rows) {
    if (!row.key) return { ok: false, message: 'Every row needs a key — enter one or remove the row.', rowIds: [row.id] };
    const identity = rules.caseInsensitiveKeys ? row.key.toLowerCase() : row.key;
    const clash = seen.get(identity);
    if (clash) {
      const scope = rules.caseInsensitiveKeys ? ' (metadata keys ignore letter case)' : '';
      return { ok: false, message: `Duplicate key "${row.key}"${scope} — rename or remove one of the rows.`, rowIds: [clash, row.id] };
    }
    seen.set(identity, row.id);
  }
  return { ok: true, value: Object.fromEntries(rows.map((row) => [row.key, row.value])) };
}

export function addKeyValueRow(rows: KeyValueRow[], rules: KeyValueRules): KeyValueRow[] {
  const taken = new Set(rows.map((row) => (rules.caseInsensitiveKeys ? row.key.toLowerCase() : row.key)));
  let index = rows.length + 1;
  while (taken.has(`key${index}`)) index += 1;
  return [...rows, { id: newRowId(), key: `key${index}`, value: '' }];
}
